/**
 * Integrations (Settings → Integrations, Slack's incoming webhooks): a webhook posts into one conversation through a
 * secret URL, `POST /v1/hooks/:ws/:webhookId/:secret` with JSON `{ "text": "…" }` (optionally `"username"` to show
 * another name), no other credentials. Messages are written as the person who added it (their access applies; they
 * must still be in the conversation) and shown as the webhook (`Message.app`). The secret is stored hashed, so the
 * full URL is shown once — on create and on "Regenerate URL", which also invalidates the old one.
 *
 * Members (not guests) add webhooks to conversations they are in; they see and manage their own, owners and admins
 * see and manage every one. Connected agents are workspace members (`Member.kind === "agent"`), listed from the
 * directory, so they have no route here.
 */
import { z } from "zod";
import { KitBlocks } from "./blockKit.ts";
import { Id, Timestamp } from "./entities.ts";

/** Webhooks per workspace (Slack has no small limit; this bounds the list). */
export const MAX_WEBHOOKS = 100;

export const IncomingWebhook = z.object({
  id: Id,
  channelId: Id,
  /** Shown as the author of what it posts. */
  name: z.string().min(1).max(80),
  createdBy: Id,
  createdAt: Timestamp,
  /** Last time it posted. */
  lastUsedAt: Timestamp.optional(),
});
export type IncomingWebhook = z.infer<typeof IncomingWebhook>;

/** GET /v1/w/:ws/webhooks — mine (all of them for owners and admins), newest first. */
export const ListWebhooksResponse = z.object({ webhooks: z.array(IncomingWebhook) });
export type ListWebhooksResponse = z.infer<typeof ListWebhooksResponse>;

/** POST /v1/w/:ws/webhooks — add one to a conversation I'm in (not archived). */
export const CreateWebhookRequest = z.object({ channelId: Id, name: z.string().trim().min(1).max(80) });
export type CreateWebhookRequest = z.infer<typeof CreateWebhookRequest>;

/** PATCH /v1/w/:ws/webhooks/:webhookId — rename and/or move it to another conversation I'm in. */
export const UpdateWebhookRequest = z.object({ name: z.string().trim().min(1).max(80).optional(), channelId: Id.optional() });
export type UpdateWebhookRequest = z.infer<typeof UpdateWebhookRequest>;

export const WebhookResponse = z.object({ webhook: IncomingWebhook });
export type WebhookResponse = z.infer<typeof WebhookResponse>;

/** Create / regenerate: the webhook and its full secret URL (shown this once). */
export const WebhookSecretResponse = z.object({ webhook: IncomingWebhook, url: z.string() });
export type WebhookSecretResponse = z.infer<typeof WebhookSecretResponse>;

/** POST /v1/hooks/:ws/:webhookId/:secret — the payload (Slack-compatible subset). */
export const WebhookPayload = z
  .object({
    /** Required without `blocks`; with them it is the plain fallback (notifications, search). */
    text: z.string().min(1).max(40_000).optional(),
    /** Block Kit layout (blockKit.ts). */
    blocks: KitBlocks.min(1).optional(),
    /** Shown instead of the webhook's name for this message. */
    username: z.string().trim().min(1).max(80).optional(),
  })
  .refine((p) => p.text !== undefined || p.blocks !== undefined, { message: "text or blocks is required" });
export type WebhookPayload = z.infer<typeof WebhookPayload>;
